import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  FiSearch,
  FiGlobe,
  FiUser,
  FiShoppingCart,
  FiChevronDown,
  FiMapPin,
  FiMenu,
  FiX,
  FiClock,
  FiPackage,
  FiCalendar,
  FiList,
  FiHeart,
  FiBookmark,
  FiAward,
  FiShoppingBag,
  FiTool,
  FiHome,
  FiTruck,
} from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import "./Header.css";
import companyLogo from "../Assets/images/newLogo.png";
import LocationSearchModal from "../components/LocationSearchModal.jsx";
import { useCart } from "../utils/cartContext.js";
import { useAuth } from "../utils/AuthContext.js";
import { useSearch } from "../utils/SearchContext.js";
import { apiAxios as axios } from "../utils/apiClient.js";
import { url } from "../Constant/TextConstant";

const Header = ({
  hasShowOptions = false,
  userLocation,
  setUserLocation, // This is now the updateLocation function from useUserLocation
  showModal,
  setShowModal,
  selectedCategory,
  setSelectedCategory,
}) => {
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showOrdersMenu, setShowOrdersMenu] = useState(false);
  const [showCartPreview, setShowCartPreview] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userIpAddress, setUserIpAddress] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const headerSearchWrapperRef = useRef(null);
  const headerSearchDialogRef = useRef(null);
  const [showMobileSearchDialog, setShowMobileSearchDialog] = useState(false);
  const mobileSearchDialogRef = useRef(null);
  const mobileSearchWrapperRef = useRef(null);
  const navigate = useNavigate();
  const { cartCount } = useCart();
  const {
    isAuthenticated: isLoggedIn,
    currentUserName: userName,
    logout,
  } = useAuth();
  const { searchQuery, setSearchQuery, showSearchDialog, setShowSearchDialog } =
    useSearch();

  const handleCategoryChange = (e) => {
    const newCategory = e.target.value;
    setSelectedCategory(newCategory);
    // Also update localStorage here for redundancy
    localStorage.setItem("selectedCategory", newCategory);
  };

  // Add this effect for mobile search dialog click outside
  useEffect(() => {
    const handleMobileClickOutside = (event) => {
      if (
        showMobileSearchDialog &&
        mobileSearchWrapperRef.current &&
        mobileSearchDialogRef.current &&
        !mobileSearchWrapperRef.current.contains(event.target) &&
        !mobileSearchDialogRef.current.contains(event.target)
      ) {
        setShowMobileSearchDialog(false);
      }
    };

    document.addEventListener("mousedown", handleMobileClickOutside, true);
    return () => {
      document.removeEventListener("mousedown", handleMobileClickOutside, true);
    };
  }, [showMobileSearchDialog]);


  // Load recent searches and get IP address
  useEffect(() => {
    const loadRecentSearches = () => {
      const categoryKey = `recent-${selectedCategory}-searches`;
      const recent = localStorage.getItem(categoryKey);
      setRecentSearches(recent ? JSON.parse(recent) : []);
    };

    const getIPDetails = async () => {
      try {
        const response = await axios.get("https://api.ipify.org?format=json");
        if (response.data?.ip) {
          setUserIpAddress(response.data.ip);
        }
      } catch (err) {
      }
    };

    if (showSearchDialog) {
      loadRecentSearches();
      getIPDetails();
      if (searchQuery.trim()) {
        fetchSuggestions(searchQuery);
      }
    }
  }, [showSearchDialog, selectedCategory, searchQuery]);

  // Close search dialog when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showSearchDialog &&
        headerSearchWrapperRef.current &&
        headerSearchDialogRef.current &&
        !headerSearchWrapperRef.current.contains(event.target) &&
        !headerSearchDialogRef.current.contains(event.target)
      ) {
        setShowSearchDialog(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [showSearchDialog]);

  // Close dropdown menus when clicking outside
  useEffect(() => {
    const handleDropDownClickOutside = (event) => {
      if (
        !event.target.closest(".header-account-section") &&
        !event.target.closest(".header-orders-section") &&
        !event.target.closest(".header-cart-section")
      ) {
        setShowAccountMenu(false);
        setShowOrdersMenu(false);
        setShowCartPreview(false);
      }
    };

    document.addEventListener("click", handleDropDownClickOutside);
    return () => {
      document.removeEventListener("click", handleDropDownClickOutside);
    };
  }, []);

  const fetchSuggestions = async (query) => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let endpoint;
      switch (selectedCategory) {
        case "products":
          endpoint = "/api/SearchControlAndLike/GetProductSearchSuggestion";
          break;
        case "services":
          endpoint = "/api/SearchControlAndLike/GetServiceSearchSuggestion";
          break;
        case "shops":
          endpoint = "/api/SearchControlAndLike/GetShopSearchSuggestion"; // Add appropriate endpoint
          break;
        case "courier":
          endpoint = "/api/SearchControlAndLike/GetCourierSearchSuggestion"; // Add appropriate endpoint
          break;
        default:
          endpoint = "/api/SearchControlAndLike/GetProductSearchSuggestion";
      }

      const response = await axios.get(
        `${url.baseAPIUrl}${endpoint}?searchTerm=${encodeURIComponent(query)}`,
        { headers: { Accept: "application/json" } }
      );

      if (response.status === 200 && response.data) {
        setSuggestions(
          Array.isArray(response.data) ? response.data.slice(0, 10) : []
        );
      }
    } catch (err) {
      setError("Failed to load suggestions. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const logSearchData = async (searchTerm) => {
    try {
      await axios.post(
        `${url.baseAPIUrl}/api/UserIpAddress/LogSearch`,
        {
          SearchTerm: searchTerm,
          SelectedOption: selectedCategory,
          IPAddress: userIpAddress,
        },
        { headers: { "Content-Type": "application/json" } }
      );
    } catch (err) {
    }
  };

  const handleSuggestionClick = async (suggestion) => {
    setSearchQuery(suggestion);
    saveRecentSearch(suggestion);
    await logSearchData(suggestion);
    handleSearch(null, suggestion);
  };

  const saveRecentSearch = (query) => {
    if (!query.trim()) return;

    // Get current category's recent searches
    const categoryKey = `recent-${selectedCategory}-searches`;
    const currentRecent = JSON.parse(localStorage.getItem(categoryKey)) || [];

    // Update and save only for current category
    const updatedRecent = [
      query,
      ...currentRecent.filter((item) => item !== query),
    ].slice(0, 5);

    localStorage.setItem(categoryKey, JSON.stringify(updatedRecent));
    setRecentSearches(updatedRecent);
  };

  const formatLocationDisplay = useCallback(() => {
    if (!userLocation) return "Select location";

    // First try to show sublocality + locality
    if (userLocation.details) {
      const { details } = userLocation;
      return `${details.subLocality || details.locality}${
        details.pincode ? `, ${details.pincode}` : ""
      }`.trim();
    }

    // Fallback to full address if available
    if (userLocation.address) {
      // Try to shorten the address if it's too long
      const parts = userLocation.address.split(",");
      if (parts.length > 2) {
        return `${parts[0]}, ${parts[1]}`.trim();
      }
      return userLocation.address;
    }

    // Final fallback
    return "Select location";
  }, [userLocation]);

  const handleDownloadNearme = () => {
    const isAppInstalled = false;
    // if (isAppInstalled) {
    //   window.location.href = "nearmeapp://download";
    // } else {
    window.location.href =
      "https://play.google.com/store/apps/details?id=com.nearmeind.NearMe&hl=en";
    // }
  };

  const handleSellOnNearme = () => {
    const isAppInstalled = false;
    if (isAppInstalled) {
      window.location.href = "nearmeapp://sell";
    } else {
      window.location.href =
        "https://play.google.com/store/apps/details?id=com.raja.NearMe_Business&hl=en_IN";
    }
  };

  const handleModalClose = useCallback(
    (shouldClose, newCoordinates = null) => {
      setShowModal(false);
      if (newCoordinates) {
        // Use the setUserLocation function (which is actually updateLocation from the hook)
        setUserLocation({
          coordinates: {
            latitude: newCoordinates.latitude,
            longitude: newCoordinates.longitude,
          },
          address: newCoordinates.address || "",
          details: newCoordinates.details || null,
        });
      }
    },
    [setShowModal, setUserLocation]
  );

  // In the render section, update the location display part:
  const renderLocationInfo = () => (
    <div
      className="header-delivery-info"
      onClick={() => setShowModal(true)}
      style={{ cursor: "pointer" }}
      title="Click to change delivery location"
      aria-label="Change delivery location"
    >
      <div className="header-location-icon-container">
        <FiMapPin className="header-location-icon" />
      </div>
      <div className="header-address-container">
        <div className="header-delivery-label">Deliver to</div>
        <div className="header-address">
          {userLocation ? (
            formatLocationDisplay()
          ) : (
            <span className="location-loading">Detecting location...</span>
          )}
        </div>
      </div>
    </div>
  );

const renderMobileSearchDialog = () => (
  <div
    ref={mobileSearchDialogRef}
    className={`header-mobile-search-dialog-container ${
      showMobileSearchDialog ? "open" : ""
    }`}
  >
    <div className="header-mobile-search-dialog-content">
      {isLoading && (
        <div className="header-search-dialog-loading-indicator">
          <FiSearch className="header-search-spinner" />
          Loading suggestions...
        </div>
      )}

      {error && (
        <div className="header-search-dialog-error-message">
          <FiX className="header-error-icon" />
          {error}
        </div>
      )}

      {/* Search suggestions section */}
      {!isLoading && !error && searchQuery && suggestions.length > 0 && (
        <div className="header-search-dialog-suggestions-section">
          <h4>Suggestions for "{searchQuery}"</h4>
          <ul>
            {suggestions.map((suggestion, index) => (
              <li
                key={index}
                onClick={() => {
                  handleSuggestionClick(suggestion);
                  setShowMobileSearchDialog(false);
                }}
                aria-label={`Search for ${suggestion}`}
              >
                <FiSearch className="header-suggestion-icon" />
                <span>{suggestion}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recent searches section */}
      {!isLoading && !error && !searchQuery && recentSearches.length > 0 && (
        <div className="header-search-dialog-recent-searches-section">
          <h4>Recent {selectedCategory} searches</h4>
          <ul>
            {recentSearches.map((search, index) => (
              <li
                key={index}
                onClick={() => {
                  handleSuggestionClick(search);
                  setShowMobileSearchDialog(false);
                }}
                aria-label={`Search for ${search}`}
              >
                <FiClock className="header-recent-icon" />
                <span>{search}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* No results section */}
      {!isLoading && !error && searchQuery && suggestions.length === 0 && (
        <div className="header-search-dialog-no-results">
          <FiSearch className="header-no-results-icon" />
          No suggestions found for "{searchQuery}"
        </div>
      )}
    </div>
  </div>
);

  const handleLogout = useCallback(async () => {
    await logout();
    setShowAccountMenu(false);
  }, [logout]);

  const handleSearch = useCallback(
    (e, query = null) => {
      e?.preventDefault();
      const searchTerm = query || searchQuery.trim();

      if (!searchTerm) return; // Exit if empty search

      // Check user location (if needed)
      if (!userLocation) {
        setShowModal(true);
        return;
      }

      // Define route mappings for cleaner code
      const routeMap = {
        products: "/searchProduct",
        services: "/searchService",
        // Add more categories as needed:
        // jobs: "/searchJobs",
        // deals: "/searchDeals",
      };

      // Get the base route based on selectedCategory
      const baseRoute = routeMap[selectedCategory];

      if (baseRoute) {
        navigate(`${baseRoute}/${encodeURIComponent(searchTerm)}`);
      } else {
      }

      // Close search dialogs
      setShowSearchDialog(false);
      setShowMobileSearch(false);
    },
    [searchQuery, selectedCategory, navigate, userLocation, setShowModal]
  );
  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  const toggleMobileSearch = useCallback(() => {
    setShowMobileSearch((prev) => !prev);
  }, []);

const renderSearchDialog = () => (
  <div
    ref={headerSearchDialogRef}
    className={`header-search-dialog-container ${
      showSearchDialog ? "open" : ""
    }`}
  >
    <div className="header-search-dialog-content">
      {isLoading && (
        <div className="header-search-dialog-loading-indicator">
          <FiSearch className="header-search-spinner" />
          Loading suggestions...
        </div>
      )}

      {error && (
        <div className="header-search-dialog-error-message">
          <FiX className="header-error-icon" />
          {error}
        </div>
      )}

      {!isLoading && !error && searchQuery && suggestions.length > 0 && (
        <div className="header-search-dialog-suggestions-section">
          <h4>Suggestions for "{searchQuery}"</h4>
          <ul>
            {suggestions.map((suggestion, index) => (
              <li
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                aria-label={`Search for ${suggestion}`}
              >
                <FiSearch className="header-suggestion-icon" />
                <span>{suggestion}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!isLoading && !error && !searchQuery && recentSearches.length > 0 && (
        <div className="header-search-dialog-recent-searches-section">
          <h4>Recent {selectedCategory} searches</h4>
          <ul>
            {recentSearches.map((search, index) => (
              <li
                key={index}
                onClick={() => handleSuggestionClick(search)}
                aria-label={`Search for ${search}`}
              >
                <FiClock className="header-recent-icon" />
                <span>{search}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!isLoading && !error && searchQuery && suggestions.length === 0 && (
        <div className="header-search-dialog-no-results">
          <FiSearch className="header-no-results-icon" />
          No suggestions found for "{searchQuery}"
        </div>
      )}
    </div>
  </div>
);

  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  };

  return (
    <header className="header">
      {/* Top Bar */}
      <div className="header-top-bar">
        <div className="header-container">
          <div className="header-welcome-text">Welcome to NearMe!</div>
          <div className="header-top-links">
            <Link to="/help-center">Help Center</Link>
            <div onClick={handleSellOnNearme}>Sell on NearMe</div>
            <div onClick={handleDownloadNearme}>Download App</div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="header-main">
        <div className="header-container">
          {/* Mobile Menu Button */}
          <button
            className="header-mobile-menu-button"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>

          {/* Logo Section */}
          <div className="header-logo-section">
            <div className="header-logo">
              <Link to="/">
                <img
                  src={companyLogo}
                  alt="NearMe Logo"
                  className="header-logo-image"
                />
              </Link>
            </div>
          </div>

          {/* Location Section */}
          <div className="header-location-section">{renderLocationInfo()}</div>

          {/* Search Section */}
          <div className="header-search-section">
            <form className="header-search-container" onSubmit={handleSearch}>
              {hasShowOptions && (
                <select
                  className="header-search-categories"
                  value={selectedCategory}
                  onChange={handleCategoryChange} // Updated to use the new handler
                  aria-label="Search category"
                >
                  <option value="products">Products</option>
                  <option value="services">Services</option>
                </select>
              )}

              <div
                ref={headerSearchWrapperRef}
                className="header-search-input-wrapper"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSearchDialog(true);
                }}
              >
                <input
                  type="search"
                  placeholder="Search NearMe"
                  className="header-search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search input"
                />
                <button
                  type="submit"
                  className="header-search-button"
                  disabled={!searchQuery.trim()}
                  aria-label="Search"
                >
                  <FiSearch />
                </button>
              </div>
            </form>
            {renderSearchDialog()}
          </div>

          {/* Right Section */}
          <div className="header-right-section">
            {/* Account Section */}
            <div
              className="header-item header-account-section"
              onMouseEnter={() => setShowAccountMenu(true)}
              onMouseLeave={() => setShowAccountMenu(false)}
              onClick={(e) => {
                e.stopPropagation();
                setShowAccountMenu(!showAccountMenu);
              }}
            >
              <div className="header-account-text">
                <div className="header-greeting">
                  Hello, {isLoggedIn ? userName : "Sign In"}
                </div>
                <div className="header-account-type">
                  Account & Lists
                  <FiChevronDown className="header-dropdown-chevron" />
                </div>
              </div>

              {showAccountMenu && (
                <div className="header-dropdown-menu header-account-menu">
                  {isLoggedIn ? (
                    <>
                      <div className="header-menu-title">Your Account</div>
                      <Link to="/savedShops" className="header-menu-item">
                        Saved Shops
                      </Link>
                      <Link to="/savedProducts" className="header-menu-item">
                        Saved Products
                      </Link>
                      <Link to="/reward" className="header-menu-item">
                        My Reward
                      </Link>
                      <div className="header-menu-divider"></div>

                      <Link to="/change-password" className="header-menu-item">
                        Change Password & Number
                      </Link>
                      <div className="header-menu-divider"></div>

                      <button
                        className="header-menu-item header-logout-button"
                        onClick={handleLogout}
                      >
                        Log Out
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="header-menu-title">Welcome</div>
                      <Link to="/login">
                        <button className="header-login-button">Sign In</button>
                      </Link>
                      <div className="header-menu-divider"></div>
                      <Link to="/signup" className="header-menu-item">
                        New Customer? Start here.
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Orders & Returns */}
            <div
              className="header-item header-orders-section"
              onMouseEnter={() => setShowOrdersMenu(true)}
              onMouseLeave={() => setShowOrdersMenu(false)}
              onClick={(e) => {
                e.stopPropagation();
                setShowOrdersMenu(!showOrdersMenu);
              }}
            >
              <div className="header-orders-text">
                <div className="header-returns">Returns</div>
                <div className="header-orders">& Orders</div>
              </div>

              {showOrdersMenu && (
                <div className="header-dropdown-menu header-orders-menu">
                  <div className="header-menu-title">Your Orders</div>
                  <Link to="/myorder" className="header-menu-item">
                    My Orders
                  </Link>
                  <Link
                    to="/serviceBookingHistory"
                    className="header-menu-item"
                  >
                    My Booking
                  </Link>
                  <Link to="/listOrder" className="header-menu-item">
                    List Order
                  </Link>
                </div>
              )}
            </div>

            {/* Cart Section */}
            <div
              className="header-item header-cart-section"
              onMouseEnter={() => setShowCartPreview(true)}
              onMouseLeave={() => setShowCartPreview(false)}
              onClick={(e) => {
                e.stopPropagation();
                navigate("/cartScreen");
              }}
            >
              <div className="header-cart-icon-container">
                <div className="header-cart-count-wrapper">
                  <FiShoppingCart className="header-cart-icon" />
                  {cartCount > 0 && (
                    <span className="header-cart-count">{cartCount}</span>
                  )}
                </div>
                <div className="header-cart-text">Cart</div>
              </div>

              {showCartPreview && (
                <div className="header-dropdown-menu header-cart-preview">
                  <div className="header-menu-title">Your Shopping Cart</div>
                  {cartCount > 0 ? (
                    <>
                      <div className="header-cart-items-preview">
                        <p>{cartCount} item(s) in cart</p>
                      </div>
                      <Link
                        to="/cartScreen"
                        className="header-view-cart-button"
                      >
                        View Cart
                      </Link>
                    </>
                  ) : (
                    <div className="header-empty-cart">
                      <FiShoppingCart className="header-empty-cart-icon" />
                      <p>Your NearMe Cart is empty</p>
                      <button
                        className="header-shop-now-button"
                        onClick={() => navigate("/products")}
                      >
                        Shop Now
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Search Button */}
          <button
            className="header-mobile-search-button"
            onClick={toggleMobileSearch}
            aria-label="Toggle search"
          >
            <FiSearch size={20} />
          </button>
        </div>

        {/* Mobile Search */}
        {showMobileSearch && (
          <div className="header-mobile-search">
            <form className="header-search-container" onSubmit={handleSearch}>
              {hasShowOptions && (
                <select
                  className="header-search-categories"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  aria-label="Search category"
                >
                  <option value="products">Products</option>
                  <option value="services">Services</option>
                </select>
              )}

              <div
                ref={mobileSearchWrapperRef}
                className="header-search-input-wrapper"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMobileSearchDialog(true);
                  if (searchQuery.trim()) {
                    fetchSuggestions(searchQuery);
                  }
                }}
              >
                <input
                  type="search"
                  placeholder="What are you looking for?"
                  className="header-search-input"
                  value={searchQuery}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchQuery(value);
                    setShowMobileSearchDialog(true);
                    if (value.trim()) {
                      fetchSuggestions(value);
                    } else {
                      setSuggestions([]);
                    }
                  }}
                  aria-label="Search products and services"
                />
                <button
                  type="submit"
                  className="header-search-button"
                  aria-label="Search"
                  disabled={!searchQuery.trim()}
                >
                  <FiSearch />
                </button>
              </div>
            </form>
            {renderMobileSearchDialog()}
          </div>
        )}

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className={`header-mobile-menu ${isOpen ? "open" : ""}`}>
            <div className="header-mobile-menu-container">
              <button
                className="header-mobile-menu-close"
                onClick={toggleMobileMenu}
                aria-label="Close Menu"
              >
                &times;
              </button>

              {/* Moved Location Section to top */}
              <div className="header-mobile-menu-section">
                <h3>Location</h3>
                <div
                  className="header-mobile-menu-item"
                  onClick={() => {
                    setShowModal(true);
                    toggleMobileMenu();
                  }}
                >
                  <FiMapPin className="header-mobile-menu-icon" />
                  {formatLocationDisplay()}
                </div>
              </div>

              <div className="header-mobile-menu-section">
                <h3>Account</h3>
                <Link
                  to={isLoggedIn ? "" : "/login"}
                  className="header-mobile-menu-item"
                  onClick={toggleMobileMenu}
                >
                  <FiUser className="header-mobile-menu-icon" />
                  {isLoggedIn ? `Hello, ${userName}` : "Sign In"}
                </Link>
                <Link
                  to="/cartScreen"
                  className="header-mobile-menu-item"
                  onClick={toggleMobileMenu}
                >
                  <FiShoppingCart className="header-mobile-menu-icon" />
                  Cart
                </Link>
                <Link
                  to="/myorder"
                  className="header-mobile-menu-item"
                  onClick={toggleMobileMenu}
                >
                  <FiPackage className="header-mobile-menu-icon" />
                  My Orders
                </Link>
                <Link
                  to="/serviceBookingHistory"
                  className="header-mobile-menu-item"
                  onClick={toggleMobileMenu}
                >
                  <FiCalendar className="header-mobile-menu-icon" />
                  My Booking
                </Link>
                <Link
                  to="/listOrder"
                  className="header-mobile-menu-item"
                  onClick={toggleMobileMenu}
                >
                  <FiList className="header-mobile-menu-icon" />
                  List Order
                </Link>
                <Link
                  to="/savedShops"
                  className="header-mobile-menu-item"
                  onClick={toggleMobileMenu}
                >
                  <FiHeart className="header-mobile-menu-icon" />
                  Saved Shops
                </Link>
                <Link
                  to="/savedProducts"
                  className="header-mobile-menu-item"
                  onClick={toggleMobileMenu}
                >
                  <FiBookmark className="header-mobile-menu-icon" />
                  Saved Products
                </Link>
                <Link
                  to="/reward"
                  className="header-mobile-menu-item"
                  onClick={toggleMobileMenu}
                >
                  <FiAward className="header-mobile-menu-icon" />
                  My Reward
                </Link>
              </div>

              <div className="header-mobile-menu-section">
                <h3>Categories</h3>
                <div
                  className="header-mobile-menu-item"
                  onClick={() => {
                    setSelectedCategory("products");
                    toggleMobileMenu();
                  }}
                >
                  <FiShoppingBag className="header-mobile-menu-icon" />
                  Products
                </div>
                <div
                  className="header-mobile-menu-item"
                  onClick={() => {
                    setSelectedCategory("services");
                    toggleMobileMenu();
                  }}
                >
                  <FiTool className="header-mobile-menu-icon" />
                  Services
                </div>
              </div>

              <br />
              <button
                className="header-menu-item header-logout-button"
                onClick={handleLogout}
              >
                Log Out
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Location Search Modal */}
      <LocationSearchModal
        showModal={showModal}
        setShowModal={handleModalClose}
        setUserLocation={setUserLocation} // This passes the updateLocation function
        isLoggedIn={isLoggedIn}
        navigate={navigate}
      />
    </header>
  );
};

export default Header;
